'use client';
import { useEffect, useRef, useState } from 'react';
import {
    MotionMaybe,
    normalizeAnimationPreset,
    useMotionControls,
} from '@/utils/animation';
import { getColorScheme } from '@/utils/getColorScheme';
import { getCTA } from '@/utils/getCTA';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { isVideoUrl } from '@/utils/isVideoUrl';
import { normalizeSvgEmbedMarkup } from '@/utils/normalizeSvgEmbed';
import { parseImagePosition } from '@/utils/parseImagePosition';
import { MarkDown } from './atoms/MarkDown';
import IconValue from './atoms/IconValue';
import VideoEmbed from './atoms/VideoEmbed';

function getGridColumnsClass(numColumns) {
    if (numColumns === 1) return 'grid-cols-1';
    if (numColumns === 2) return 'grid-cols-2';
    if (numColumns === 3) return 'grid-cols-1 md:grid-cols-3';
    if (numColumns === 4) return 'grid-cols-2 md:grid-cols-2 lg:grid-cols-4';
    if (numColumns === 5) return 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5';
    return '';
}

const isEmbedCode = (val) =>
    typeof val === 'string' &&
    /<\s*(iframe|script|video|svg|div|section|figure|blockquote)[\s>]/i.test(
        val
    );

const normalizeMediaUrl = (input) => {
    if (!input) return '';
    if (typeof input === 'string') return input.trim();
    if (typeof input === 'object') {
        const candidate =
            input.Media ||
            input.media ||
            input.url ||
            input.src ||
            input.value ||
            '';
        return typeof candidate === 'string' ? candidate.trim() : '';
    }
    return '';
};

export default function ItemGrid(props) {
    const {
        Tagline,
        Header,
        Features,
        CTA,
        CustomCTALink,
        SecondaryButtonText,
        SecondaryButtonLink,
        OutlineButtonText,
        OutlineButtonLink,
        Anchor,
        ColorScheme = 'light',
        CardsColorScheme,
        VerticalSpacing = 'medium',
        Alignment = 'center',
        ImageUrl,
        ImagePosition = '50',
        BackgroundFixed = false,
        CardImageUrl,
        CardImagePosition = '50',
        Animation = 'none',
        Duration = 0.6,
        AnimationGroup = 'content',
        EditorPreview = false,
    } = props;

    const normalizedImageUrl = normalizeMediaUrl(ImageUrl);
    const backgroundIsEmbed = isEmbedCode(normalizedImageUrl);
    const backgroundVideoUrl =
        !backgroundIsEmbed && isVideoUrl(normalizedImageUrl)
            ? normalizedImageUrl
            : '';
    const backgroundImageUrl =
        backgroundVideoUrl || backgroundIsEmbed ? '' : normalizedImageUrl;
    const { x: bgPosX, y: bgPosY } = parseImagePosition(ImagePosition);
    const backgroundEmbedHtml = backgroundIsEmbed
        ? normalizeSvgEmbedMarkup(normalizedImageUrl, {
              x: bgPosX,
              y: bgPosY,
              mode: 'cover',
          })
        : '';
    const hasBackgroundMedia = Boolean(
        backgroundImageUrl || backgroundVideoUrl || backgroundIsEmbed
    );

    let numColumns = 5;
    const numItems = Features.length;
    if (numItems === 1) numColumns = 1;
    else if (numItems <= 5) numColumns = numItems;
    else if ([6, 7, 9, 11, 12].includes(numItems)) numColumns = 3;
    else if (numItems === 8) numColumns = 4;

    const section = getColorScheme(ColorScheme, props.GradientDirection);
    const {
        backgroundColor,
        headerColor,
        taglineColor,
        thirdButton,
        secondaryButton,
        overlayColor,
        backgroundStyle,
        overlayStyle,
    } = section;

    const noCardSchemeProvided = !CardsColorScheme || !CardsColorScheme.trim();

    const effectiveCardScheme = noCardSchemeProvided
        ? ColorScheme
        : CardsColorScheme.trim();

    const wantsBlur =
        !noCardSchemeProvided && effectiveCardScheme.endsWith('-blur');

    const baseCardScheme = wantsBlur
        ? effectiveCardScheme.replace(/-blur$/, '')
        : effectiveCardScheme;

    const cardBase = getColorScheme(baseCardScheme, props.GradientDirection);

    const baseBg = cardBase.boxColor || cardBase.backgroundColor || '';
    const cardText = cardBase.boxTextColor || cardBase.textColor || '';
    const cardTitleColor = cardBase.headerColor || cardText || '';
    const cardIconColor = cardBase.iconColor || cardTitleColor || '';
    const cardBorder = cardBase.boxBorder || '';

    const isCardClear = baseCardScheme.trim() === 'clear';
    const solidCardBg = isCardClear ? 'bg-transparent' : baseBg;

    const isOverflowVariant = [
        'none-overflow',
        'medium-overflow',
        'large-overflow',
        'overflow',
    ].includes(VerticalSpacing);
    const hideHeading = isOverflowVariant;

    const itemGridContentRef = useRef(null);
    const [overflowCardLift, setOverflowCardLift] = useState(0);

    let vSpace = getVerticalSpacing(VerticalSpacing);
    if (VerticalSpacing === 'overflow') {
        vSpace = 'pt-0 -mb-[56px] sm:-mb-[56px] md:-mb-[56px] lg:-mb-[72px]';
    } else if (VerticalSpacing === 'medium-overflow') {
        vSpace = 'pt-0 pb-24 lg:pb-36';
    } else if (VerticalSpacing === 'large-overflow') {
        vSpace = 'pt-0 pb-28  md:pb-40 lg:pb-56 xl:pb-60 2xl:pb-64';
    }

    const cardLiftClass = isOverflowVariant
        ? 'transform -translate-y-1/2 will-change-transform'
        : '';

    const isBackgroundFixed =
        typeof BackgroundFixed === 'string'
            ? BackgroundFixed.toLowerCase() === 'true'
            : !!BackgroundFixed;
    const backgroundFixedClass = isBackgroundFixed
        ? 'bg-scroll lg:bg-fixed'
        : '';
    const embedPositionClass = isBackgroundFixed
        ? 'absolute inset-0 lg:fixed lg:inset-0'
        : 'absolute inset-0';
    let backgroundImageStyle = {};
    if (backgroundImageUrl) {
        backgroundImageStyle = {
            backgroundImage: `url(${backgroundImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: `${bgPosX}% ${bgPosY}%`,
            backgroundRepeat: 'no-repeat',
        };
    }
    const sectionStyle = {};

    const alignmentClasses =
        Alignment === 'left'
            ? 'text-left items-start'
            : 'text-center items-center';

    // Animation setup
    const duration = Number(Duration) || 0.6;
    const normalizedGroup = (() => {
        const raw = String(AnimationGroup ?? '')
            .toLowerCase()
            .trim();
        if (['cta', 'ctas', 'button', 'buttons'].includes(raw)) return 'ctas';
        if (
            ['media', 'medias', 'image', 'images', 'background', 'bg'].includes(
                raw
            )
        )
            return 'medias';
        if (['feature', 'features', 'cards', 'items'].includes(raw))
            return 'features';
        if (raw === 'all') return 'all';
        return 'content';
    })();
    const presetGroup =
        normalizedGroup === 'features' || normalizedGroup === 'all'
            ? 'features'
            : 'content';
    const normalizedPreset = normalizeAnimationPreset(Animation, presetGroup);
    const viewportAmount =
        normalizedPreset === 'stagger' &&
        (normalizedGroup === 'features' || normalizedGroup === 'all')
            ? 0.15
            : 0.5;
    const {
        enableAnim,
        isStagger,
        containerMotionProps,
        childMotionProps,
        remountKey,
        enableContainer,
        enableChild,
    } = useMotionControls({
        preset: normalizedPreset,
        duration,
        editorPreview: EditorPreview,
        group: normalizedGroup === 'all' ? 'content' : normalizedGroup,
        keyBase: 'item-grid',
        viewportAmount,
    });
    const animateContent =
        enableAnim &&
        (normalizedGroup === 'all' || normalizedGroup === 'content');
    const animateFeatures =
        enableAnim &&
        (normalizedGroup === 'all' || normalizedGroup === 'features');
    const animateCTAs =
        enableAnim && (normalizedGroup === 'all' || normalizedGroup === 'ctas');
    const animateMedia =
        enableAnim &&
        (normalizedGroup === 'all' || normalizedGroup === 'medias');

    const featureMotionBase =
        normalizedPreset === 'zoom'
            ? {
                  ...childMotionProps,
                  variants: {
                      offscreen: { opacity: 0, scale: 0.96 },
                      onscreen: {
                          opacity: 1,
                          scale: 1,
                          transition: {
                              type: 'tween',
                              duration: Math.max(0.2, duration),
                          },
                      },
                  },
              }
            : childMotionProps;
    const featureChildMotionProps =
        isStagger && animateFeatures
            ? {
                  variants: {
                      hidden: { opacity: 0, y: 16 },
                      show: {
                          opacity: 1,
                          y: 0,
                          transition: {
                              duration: Math.max(0.4, duration - 0.2),
                          },
                      },
                  },
              }
            : featureMotionBase;

    const featureContainerEnabled =
        animateFeatures &&
        isStagger &&
        (normalizedGroup === 'all' ? enableAnim : enableContainer('features'));
    const featureChildEnabled =
        animateFeatures &&
        (normalizedGroup === 'all' ? enableAnim : enableChild('features'));
    const contentChildEnabled =
        animateContent &&
        (normalizedGroup === 'all' ? enableAnim : enableChild('content'));
    const mediaChildEnabled =
        animateMedia &&
        (normalizedGroup === 'all' ? enableAnim : enableChild('medias'));

    const ctaEnabled =
        animateCTAs &&
        (normalizedGroup === 'all' ? enableAnim : enableChild('ctas'));

    const { hasAny: hasCTAs, element: CTAs } = getCTA(
        {
            ...props,
            CTA,
            CustomCTALink,
            SecondaryButtonText,
            SecondaryButtonLink,
            OutlineButtonText,
            OutlineButtonLink,
        },
        {
            primaryVariant: 'primary',
            secondaryVariant: secondaryButton,
            outlineVariant: thirdButton,
            buttonAlign: 'justify-center',
            wrapSpacing: 'mt-10 lg:mt-16',
            buttonsClassName:
                'flex flex-wrap items-center justify-center gap-4',
            enableAnim: ctaEnabled,
            motionProps: {
                ...childMotionProps,
                key: `${remountKey}-ctas`,
            },
        }
    );

    useEffect(() => {
        if (!isOverflowVariant || !hasCTAs) {
            setOverflowCardLift(0);
            return undefined;
        }

        const content = itemGridContentRef.current;
        const grid = content?.querySelector(
            '[data-itemgrid-feature-grid="true"]'
        );
        const cards = grid?.querySelectorAll(
            '[data-itemgrid-feature-card="true"]'
        );
        const lastCard = cards?.[cards.length - 1];
        if (!lastCard) return undefined;

        const measure = () => {
            const height = lastCard.getBoundingClientRect().height;
            setOverflowCardLift((prev) => {
                const next = Math.round(height / 2);
                return prev === next ? prev : next;
            });
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(lastCard);
        window.addEventListener('resize', measure);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [hasCTAs, isOverflowVariant, numItems]);

    return (
        <section
            id={Anchor}
            className={`editable-component relative px-6 lg:px-8 ${backgroundColor} ${vSpace} ${backgroundFixedClass}`}
            style={{ ...backgroundStyle, ...sectionStyle }}
            data-json="itemgrid"
        >
            {hasBackgroundMedia && (
                <MotionMaybe
                    enable={mediaChildEnabled}
                    nodeKey={`${remountKey}-media`}
                    motionProps={childMotionProps}
                    className="absolute inset-0"
                >
                    {backgroundVideoUrl ? (
                        <div className="absolute inset-0 overflow-hidden">
                            <VideoEmbed
                                url={backgroundVideoUrl}
                                playing
                                muted
                                loop
                                controls={false}
                                width="100%"
                                height="100%"
                                className="pointer-events-none absolute inset-0 h-full w-full"
                                playsinline
                            />
                        </div>
                    ) : backgroundIsEmbed ? (
                        <div className="pointer-events-none absolute inset-0 overflow-hidden">
                            <div
                                className={`${embedPositionClass} [&>*]:h-full [&>*]:max-h-none [&>*]:w-full [&>*]:max-w-none [&>svg]:block`}
                                dangerouslySetInnerHTML={{
                                    __html: backgroundEmbedHtml,
                                }}
                            />
                        </div>
                    ) : (
                        <div
                            className={`absolute inset-0 ${backgroundFixedClass}`}
                            style={backgroundImageStyle}
                        />
                    )}
                    <div
                        className={`absolute inset-0 ${overlayColor || ''}`}
                        style={overlayStyle}
                    />
                </MotionMaybe>
            )}

            <div
                ref={itemGridContentRef}
                className={`relative z-40 mx-auto flex max-w-7xl flex-col ${alignmentClasses}`}
            >
                {!hideHeading && Header && (
                    <MotionMaybe
                        enable={contentChildEnabled}
                        nodeKey={`${remountKey}-heading`}
                        motionProps={childMotionProps}
                        className={`mb-8 max-w-3xl ${
                            Alignment === 'left' ? 'ml-4' : 'mx-auto'
                        }`}
                    >
                        <div className="mb-6">
                            <MarkDown
                                components={{
                                    p: ({ children, className, ...rest }) => (
                                        <p
                                            {...rest}
                                            className={[
                                                'font-display text-base uppercase leading-7 tracking-wide',
                                                taglineColor,
                                                className,
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                        >
                                            {children}
                                        </p>
                                    ),
                                }}
                            >
                                {Tagline}
                            </MarkDown>
                            <MarkDown
                                variant="h2"
                                className={`max-w-3xl font-display text-2xl tracking-tight ${headerColor} md:text-3xl lg:text-4xl`}
                            >
                                {Header}
                            </MarkDown>
                        </div>
                    </MotionMaybe>
                )}

                {/* GRID */}
                <MotionMaybe
                    enable={featureContainerEnabled}
                    nodeKey={`${remountKey}-grid`}
                    motionProps={containerMotionProps}
                    data-itemgrid-feature-grid="true"
                    className={`grid gap-x-2 gap-y-4 md:gap-4 ${getGridColumnsClass(
                        numColumns
                    )} ${
                        numItems === 1
                            ? 'place-items-center justify-center'
                            : ''
                    }`}
                    style={{ overflow: 'visible' }}
                >
                    {Features.slice(0, 15).map((Feature, index) => {
                        const wrapperBase =
                            `${cardBorder} h-full rounded-[min(1.5rem,var(--shape-radius))] ` +
                            `${
                                numItems === 1 || numItems === 2
                                    ? 'w-full max-w-lg'
                                    : ''
                            }`;

                        const cardMediaRaw = normalizeMediaUrl(
                            Feature.CardImageUrl || CardImageUrl || ''
                        );
                        const cardMediaIsEmbed = isEmbedCode(cardMediaRaw);
                        const cardVideoUrl =
                            !cardMediaIsEmbed && isVideoUrl(cardMediaRaw)
                                ? cardMediaRaw
                                : '';
                        const cardEmbedHtml = cardMediaIsEmbed
                            ? cardMediaRaw
                            : '';
                        const cardImageUrl =
                            cardVideoUrl || cardEmbedHtml ? '' : cardMediaRaw;
                        const hasCardBgImage = Boolean(
                            cardImageUrl || cardVideoUrl || cardEmbedHtml
                        );
                        const cardImagePositionRaw =
                            Feature.CardImagePosition ||
                            CardImagePosition ||
                            '50';
                        const { x: cardPosX, y: cardPosY } =
                            parseImagePosition(cardImagePositionRaw);
                        const normalizedCardEmbedHtml = cardEmbedHtml
                            ? normalizeSvgEmbedMarkup(cardEmbedHtml, {
                                  x: cardPosX,
                                  y: cardPosY,
                                  mode: 'cover',
                              })
                            : '';

                        const wrapperStyle = cardImageUrl
                            ? {
                                  backgroundImage: `url(${cardImageUrl})`,
                                  backgroundSize: 'cover',
                                  backgroundPosition: `${cardPosX}% ${cardPosY}%`,
                                  backgroundRepeat: 'no-repeat',
                              }
                            : undefined;

                        const wrapperClasses = hasCardBgImage
                            ? `${wrapperBase} relative overflow-hidden`
                            : wantsBlur
                            ? `${wrapperBase} relative overflow-visible`
                            : `${solidCardBg} ${wrapperBase}`;

                        const usesLightForeground =
                            /(?:^|\s)text-(?:white|neutral-(?:50|100))(?:\s|$)/.test(
                                `${cardText} ${cardTitleColor} ${cardIconColor}`
                            );
                        const textColorForCard = hasCardBgImage
                            ? usesLightForeground
                                ? 'text-white'
                                : cardText || cardTitleColor
                            : cardText;
                        const titleColorForCard = hasCardBgImage
                            ? usesLightForeground
                                ? 'text-white'
                                : cardTitleColor || cardText
                            : cardTitleColor;
                        const iconColorForCard = hasCardBgImage
                            ? usesLightForeground
                                ? 'text-white'
                                : cardIconColor || cardTitleColor || cardText
                            : cardIconColor;

                        const overlayTintClass = cardBase.overlayColor || '';
                        const overlayNeeded =
                            hasCardBgImage || (wantsBlur && !isCardClear);
                        const overlayColorClass = hasCardBgImage
                            ? overlayTintClass ||
                              (baseBg
                                  ? `${baseBg} bg-opacity-80`
                                  : 'bg-black/40')
                            : overlayTintClass || baseBg;
                        const overlayBlur =
                            !hasCardBgImage && wantsBlur && !isCardClear
                                ? 'backdrop-blur-sm'
                                : '';
                        const overlayOpacity =
                            !hasCardBgImage && wantsBlur && !isCardClear
                                ? 'opacity-80'
                                : '';
                        const readabilityOverlayClass =
                            hasCardBgImage && usesLightForeground
                                ? 'bg-gradient-to-t from-black/70 via-black/35 to-transparent'
                                : '';
                        const shouldClipCardText =
                            hasCardBgImage && usesLightForeground;
                        const clippedLightTextStyle = shouldClipCardText
                            ? {
                                  backgroundImage:
                                      'linear-gradient(#ffffff, #ffffff)',
                                  backgroundClip: 'text',
                                  WebkitBackgroundClip: 'text',
                                  color: 'transparent',
                                  WebkitTextFillColor: 'transparent',
                              }
                            : undefined;

                        const cardMediaLayer = cardVideoUrl ? (
                            <VideoEmbed
                                url={cardVideoUrl}
                                playing
                                muted
                                loop
                                controls={false}
                                width="100%"
                                height="100%"
                                className="pointer-events-none absolute inset-0 h-full w-full"
                                playsinline
                            />
                        ) : cardEmbedHtml ? (
                            <div
                                className="pointer-events-none absolute inset-0 [&>*]:h-full [&>*]:max-h-none [&>*]:w-full [&>*]:max-w-none [&>svg]:block"
                                dangerouslySetInnerHTML={{
                                    __html: normalizedCardEmbedHtml,
                                }}
                            />
                        ) : null;

                        const Overlay = overlayNeeded ? (
                            <div
                                className={`pointer-events-none absolute inset-0 ${overlayColorClass} rounded-[min(1.5rem,var(--shape-radius))] ${overlayOpacity} ${overlayBlur}`}
                            />
                        ) : null;
                        const ReadabilityOverlay = readabilityOverlayClass ? (
                            <div
                                className={`pointer-events-none absolute inset-0 rounded-[min(1.5rem,var(--shape-radius))] ${readabilityOverlayClass}`}
                            />
                        ) : null;

                        const LinkedInner = (
                            <div
                                className={`flex h-full flex-col rounded-[min(1.5rem,var(--shape-radius))] p-3 transition-all duration-200 hover:bg-black/10 lg:p-5 ${
                                    overlayNeeded || hasCardBgImage
                                        ? 'relative z-10'
                                        : ''
                                }`}
                            >
                                <div
                                    className={`${textColorForCard} text-base leading-4`}
                                >
                                    <div
                                        className={`items-bottom mb-3 flex ${
                                            Alignment === 'left'
                                                ? 'justify-start'
                                                : 'justify-center'
                                        } bg-transparent`}
                                    >
                                        <IconValue
                                            value={Feature.Icon}
                                            className={`text-5xl ${iconColorForCard}`}
                                            style={{
                                                fontSize:
                                                    Feature.size || '3rem',
                                            }}
                                        />
                                    </div>
                                    <MarkDown
                                        components={{
                                            p: ({
                                                children,
                                                className,
                                                node,
                                                ...rest
                                            }) => {
                                                void node;
                                                return (
                                                    <div
                                                        {...rest}
                                                        className={[
                                                            'font-display text-base leading-6 sm:text-lg md:text-xl',
                                                            Alignment === 'left'
                                                                ? 'text-left'
                                                                : 'text-center',
                                                            shouldClipCardText
                                                                ? ''
                                                                : titleColorForCard,
                                                            className,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(' ')}
                                                        style={
                                                            clippedLightTextStyle
                                                        }
                                                    >
                                                        {children}
                                                    </div>
                                                );
                                            },
                                        }}
                                    >
                                        {Feature.Title}
                                    </MarkDown>
                                </div>

                                {Boolean(Feature.Description?.trim()) && (
                                    <div
                                        className={`${textColorForCard} mt-3 flex flex-auto flex-col text-xs font-text leading-5 sm:text-base ${
                                            Alignment === 'left'
                                                ? 'text-left'
                                                : 'text-center'
                                        }`}
                                    >
                                        <MarkDown
                                            className="flex-auto"
                                            components={{
                                                p: ({
                                                    children,
                                                    className,
                                                    node,
                                                    ...rest
                                                }) => {
                                                    void node;
                                                    return (
                                                        <p
                                                            {...rest}
                                                            className={[
                                                                'mb-4 whitespace-pre-wrap last:mb-0',
                                                                shouldClipCardText
                                                                    ? ''
                                                                    : textColorForCard,
                                                                className,
                                                            ]
                                                                .filter(Boolean)
                                                                .join(' ')}
                                                            style={
                                                                clippedLightTextStyle
                                                            }
                                                        >
                                                            {children}
                                                        </p>
                                                    );
                                                },
                                            }}
                                        >
                                            {Feature.Description}
                                        </MarkDown>
                                    </div>
                                )}
                            </div>
                        );

                        const PlainInner = (
                            <div
                                className={`flex h-full flex-col rounded-[min(1.5rem,var(--shape-radius))] p-6 lg:p-8 ${
                                    overlayNeeded || hasCardBgImage
                                        ? 'relative z-10'
                                        : ''
                                }`}
                            >
                                <div
                                    className={`${textColorForCard} text-base leading-4`}
                                >
                                    <div
                                        className={`items-bottom mb-3 flex h-16 ${
                                            Alignment === 'left'
                                                ? 'justify-start'
                                                : 'justify-center'
                                        } bg-transparent`}
                                    >
                                        <IconValue
                                            value={Feature.Icon}
                                            className={`text-5xl ${iconColorForCard}`}
                                            style={{
                                                fontSize:
                                                    Feature.size || '3rem',
                                            }}
                                        />
                                    </div>
                                    <MarkDown
                                        components={{
                                            p: ({
                                                children,
                                                className,
                                                node,
                                                ...rest
                                            }) => {
                                                void node;
                                                return (
                                                    <div
                                                        {...rest}
                                                        className={[
                                                            'font-display text-base leading-6 sm:text-lg md:text-xl',
                                                            Alignment === 'left'
                                                                ? 'text-left'
                                                                : 'text-center',
                                                            Feature.Description?.trim()
                                                                ? 'mb-3'
                                                                : '',
                                                            shouldClipCardText
                                                                ? ''
                                                                : titleColorForCard,
                                                            className,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(' ')}
                                                        style={
                                                            clippedLightTextStyle
                                                        }
                                                    >
                                                        {children}
                                                    </div>
                                                );
                                            },
                                        }}
                                    >
                                        {Feature.Title}
                                    </MarkDown>
                                </div>

                                {Boolean(Feature.Description?.trim()) && (
                                    <div
                                        className={`${textColorForCard} flex flex-auto flex-col text-xs font-text leading-6 sm:text-base ${
                                            Alignment === 'left'
                                                ? 'text-left'
                                                : 'text-center'
                                        }`}
                                    >
                                        <MarkDown
                                            className="flex-auto"
                                            components={{
                                                p: ({
                                                    children,
                                                    className,
                                                    node,
                                                    ...rest
                                                }) => {
                                                    void node;
                                                    return (
                                                        <p
                                                            {...rest}
                                                            className={[
                                                                'mb-4 whitespace-pre-wrap last:mb-0',
                                                                shouldClipCardText
                                                                    ? ''
                                                                    : textColorForCard,
                                                                className,
                                                            ]
                                                                .filter(Boolean)
                                                                .join(' ')}
                                                            style={
                                                                clippedLightTextStyle
                                                            }
                                                        >
                                                            {children}
                                                        </p>
                                                    );
                                                },
                                            }}
                                        >
                                            {Feature.Description}
                                        </MarkDown>
                                    </div>
                                )}
                            </div>
                        );

                        return (
                            <div
                                data-itemgrid-feature-card="true"
                                className={`${cardLiftClass} h-full`}
                                key={Feature.Title || index}
                            >
                                <MotionMaybe
                                    enable={featureChildEnabled}
                                    nodeKey={`${remountKey}-feature-${index}`}
                                    motionProps={featureChildMotionProps}
                                    className={wrapperClasses}
                                    style={wrapperStyle}
                                >
                                    {cardMediaLayer}
                                    {Overlay}
                                    {ReadabilityOverlay}
                                    {Feature.ItemLink ? (
                                        <a
                                            href={Feature.ItemLink}
                                            className="block h-full"
                                        >
                                            {LinkedInner}
                                        </a>
                                    ) : (
                                        PlainInner
                                    )}
                                </MotionMaybe>
                            </div>
                        );
                    })}
                </MotionMaybe>

                {hasCTAs && (
                    <div
                        style={
                            isOverflowVariant && overflowCardLift
                                ? {
                                      transform: `translateY(${-overflowCardLift}px)`,
                                  }
                                : undefined
                        }
                    >
                        {CTAs}
                    </div>
                )}
            </div>
        </section>
    );
}
